import { useParams, Link } from 'react-router-dom';
import { PageMeta, JsonLd } from '@/components/feature/Head';
import { getArticleById, articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <main className="pt-20 min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500 mb-4">文章不存在或已被删除</p>
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <PageMeta
        title={`${article.title} - 国产区精选内容聚合平台`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonical={`https://guochanqu.com/article/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.coverImage}
        ogUrl={`https://guochanqu.com/article/${article.id}`}
        ogType="article"
        publishedTime={article.publishDate}
        author={article.author}
        tags={article.tags}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.publishDate,
          publisher: { '@type': 'Organization', name: '国产区精选内容聚合平台' },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://guochanqu.com' },
            { '@type': 'ListItem', position: 2, name: article.category, item: `https://guochanqu.com/category/${article.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: article.title, item: `https://guochanqu.com/article/${article.id}` },
          ],
        }}
      />

      <main className="pt-20 min-h-screen bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line mr-1"></i>首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${article.categorySlug}`}
              className="hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              {article.category}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 line-clamp-1">{article.title}</span>
          </nav>

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-[10px] px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {article.category}
              </Link>
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()} 次阅读
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-auto max-h-[500px] object-cover"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 py-10 border-t border-background-200/70">
            <h3 className="text-lg font-heading text-foreground-950 mb-6">相关推荐</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={rel.coverImage}
                      alt={rel.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {rel.title}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-2">{rel.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </>
  );
}